import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AssetGrid, SearchBar, SmartSearchChips } from '@shared/components'
import { getAssetRepository, isSentence, smartFor, understandSearch, type UnderstoodSearch } from '@shared/services'
import { useSelection } from '@shared/state/SelectionContext'
import type { Asset, LibraryKey } from '@shared/types'
import type { AssetCursor } from '@shared/types'
import { Drift, fromAsset, preload, type SourcePicture } from './MoodboardStart'
import { PillPicker } from '../components/PillPicker'
import { PictureTagsDialog } from '../components/PictureTagsDialog'
import { PictureViewer } from '../components/PictureViewer'
import { useAuth } from '@shared/state/AuthContext'
import { libraryOfAsset, noteLibrary } from '@shared/state/assetLibraries'
import { useRemembered } from '../useRemembered'
import './moodboard.css'
import './amazon.css'
import './web-images.css'

/**
 * Internal Library (Huanrui, 2026-10-13): every library at once
 * -------------------------------------------------------------
 * A Starter card. Opens on one search line over pictures from all three
 * libraries drifting behind (the Mood board's look); a search shows every
 * picture that matches, from Audit, Visual Language and Design References
 * together -- not split by library, and with no filters, only the pictures.
 * Picking works as in every library: the tray at the foot sends to Miro,
 * copies to a project and the rest (SelectionTray.tsx).
 */

export const INTERNAL_LIBRARY_ROUTE = '/internal-library'
const LIBRARIES: LibraryKey[] = ['audit', 'visual-language', 'design-works']

/**
 * Which libraries a search covers (Huanrui, 2026-10-14): all three, or one,
 * chosen from a small menu in front of the search box. Remembered.
 */
type Scope = 'all' | 'audit' | 'design-works' | 'visual-language'
const SCOPES: { value: Scope; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'audit', label: 'Audit' },
  { value: 'design-works', label: 'Reference' },
  { value: 'visual-language', label: 'Language' },
]
const librariesOf = (scope: Scope): LibraryKey[] => (scope === 'all' ? LIBRARIES : [scope])
const isScope = (value: unknown): value is Scope => SCOPES.some((one) => one.value === value)
const PAGE = 40

/**
 * A sentence understood (Huanrui, 2026-11-02): per library, the tags it
 * means, and English words for the rest. Searched with these, pictures are
 * scored and shown best first (docs/smart-search.sql); with none of either,
 * the ordinary search is used.
 */
type Plan = UnderstoodSearch
const planIsEmpty = (plan: Plan | null) =>
  !plan || (plan.words.length === 0 && Object.values(plan.libraries).every((tags) => !tags || tags.length === 0))

interface Source {
  library: LibraryKey
  next: AssetCursor | null
  done: boolean
  total: number
}

export function InternalLibraryPage() {
  const repository = useMemo(() => getAssetRepository(), [])
  const { selectedIds, toggle, select, deselect, selectMany, deselectMany } = useSelection()

  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  /** Long press on a picture: its tags, in its own library (administrators, as on every library page). */
  const [tagging, setTagging] = useState<Asset | null>(null)
  /** A picture opened larger from its card's View larger button (2026-10-15). */
  const [viewing, setViewing] = useState<Asset | null>(null)
  // The way back up, as on every library page.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 700)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const [query, setQuery] = useState('')
  const [kept, keepScope] = useRemembered<string>('ww.internal.scope', 'all')
  const scope: Scope = isScope(kept) ? kept : 'all'
  const [asked, setAsked] = useState('')
  const [assets, setAssets] = useState<Asset[]>([])
  const [sources, setSources] = useState<Source[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')
  const run = useRef(0)
  /** What the sentence was understood as, shown under the box; null for a plain search. */
  const [plan, setPlan] = useState<Plan | null>(null)
  const planRef = useRef<Plan | null>(null)
  const [understanding, setUnderstanding] = useState(false)

  // Behind the search: pictures from every library, shuffled.
  //
  // Sooner (Huanrui, 2026-10-14: the black screen lasted too long). The
  // backdrop of the last visit is kept for an hour and goes up at once; a
  // fresh one is asked for every time, for next time. With nothing kept, the
  // libraries that have answered within 1.2 s are used, the drift fades in
  // after at most 0.6 s of preloading, and the fade itself is quicker.
  const [backdrop, setBackdrop] = useState<SourcePicture[]>([])
  const [driftReady, setDriftReady] = useState(false)
  useEffect(() => {
    let shown = false
    const show = (pictures: SourcePicture[]) => {
      if (shown || pictures.length === 0) return
      shown = true
      setBackdrop(pictures)
      void preload(pictures.slice(0, 21), 600).then(() => setDriftReady(true))
    }
    const kept = readBackdrop()
    if (kept) show(kept)

    const lists: Asset[][] = []
    const shuffled = () => {
      const seen = new Set<string>()
      const all = lists.flat().filter((asset) => !seen.has(asset.id) && seen.add(asset.id))
      for (let i = all.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[all[i], all[j]] = [all[j], all[i]]
      }
      return all.slice(0, 84).map(fromAsset)
    }
    const deadline = window.setTimeout(() => show(shuffled()), 1200)
    void Promise.all(
      LIBRARIES.map((library) =>
        repository
          .search({ library, limit: 40 })
          .then((page) => {
            lists.push(page.items)
          })
          .catch(() => undefined),
      ),
    ).then(() => {
      window.clearTimeout(deadline)
      const pictures = shuffled()
      show(pictures)
      keepBackdrop(pictures)
    })
    return () => window.clearTimeout(deadline)
  }, [repository])

  /** The next page from every library that has more, taken in turns. */
  const fetchMore = useCallback(
    async (text: string, from: Source[], seen: Set<string>) => {
      const understood = planRef.current
      const pages = await Promise.all(
        from.map(async (source) => {
          if (source.done) return { source, items: [] as Asset[] }
          // The weights and the core score with it (smartFor): built by hand here,
          // they were left behind, and the page searched as if every tag were
          // worth the same (2026-11-02).
          const smart = understood ? smartFor(understood, source.library) : null
          // Nothing understood for this library: it has nothing to score by.
          if (understood && !smart) {
            return { source: { ...source, next: null, done: true, total: 0 }, items: [] as Asset[] }
          }
          const page = await repository.search({ text, library: source.library, limit: PAGE, after: source.next, smart })
          return {
            source: { ...source, next: page.next ?? null, done: !page.next, total: page.total },
            items: page.items,
          }
        }),
      )
      const added: Asset[] = []
      for (let i = 0; pages.some((page) => i < page.items.length); i += 1) {
        for (const page of pages) {
          const one = page.items[i]
          if (one && !seen.has(one.id)) {
            seen.add(one.id)
            added.push(one)
            noteLibrary(one.id, page.source.library)
          }
        }
      }
      // Best matches first across the libraries too, keeping the turns
      // within each tier (docs/search-ranked.sql, 2026-10-14) -- or, for a
      // sentence understood, the highest score first (2026-11-02).
      if (understood) added.sort((x, y) => (y.matchScore ?? 0) - (x.matchScore ?? 0))
      else added.sort((x, y) => (x.matchRank ?? 0) - (y.matchRank ?? 0))
      return { sources: pages.map((page) => page.source), added }
    },
    [repository],
  )

  const seenRef = useRef(new Set<string>())
  /**
   * `given`: a plan already in hand (a piece of it taken off under the box),
   * searched with as it is. Otherwise a sentence is understood first -- a
   * second or two, once per sentence (the Worker keeps the answer) -- and a
   * word or two is searched at once, as before.
   */
  const search = async (text = query, within: Scope = scope, given?: Plan | null) => {
    const words = text.trim()
    if (!words) return
    const mine = ++run.current
    setAsked(words)
    setLoading(true)
    setError('')
    setAssets([])
    seenRef.current = new Set()
    try {
      let understood: Plan | null = given ?? null
      setUnderstanding(false)
      if (given === undefined && isSentence(words)) {
        setUnderstanding(true)
        try {
          understood = await understandSearch(words, librariesOf(within))
        } catch (caught) {
          // Not understood: the ordinary search, which still finds what it can.
          console.warn('the search could not be understood; searching the words', caught)
          understood = null
        }
        if (mine !== run.current) return
        setUnderstanding(false)
      }
      const used = planIsEmpty(understood) ? null : understood
      planRef.current = used
      setPlan(used)
      const start: Source[] = librariesOf(within).map((library) => ({ library, next: null, done: false, total: 0 }))
      const { sources: next, added } = await fetchMore(words, start, seenRef.current)
      if (mine !== run.current) return
      setSources(next)
      setAssets(added)
    } catch (caught) {
      if (mine === run.current) setError(caught instanceof Error ? caught.message : 'The search did not work')
    } finally {
      if (mine === run.current) setLoading(false)
    }
  }

  /** A piece of what was understood taken off: the rest searched again, without asking again. */
  const dropFromPlan = (piece: { tag?: string; word?: string }) => {
    const now = planRef.current
    if (!now) return
    const next: Plan = {
      ...now,
      words: now.words.filter((word) => word !== piece.word),
      libraries: Object.fromEntries(
        Object.entries(now.libraries).map(([library, tags]) => [
          library,
          (tags ?? []).filter((tag) => tag.name !== piece.tag),
        ]),
      ) as Plan['libraries'],
    }
    // Everything taken off: the sentence's own words, searched plainly.
    void search(asked, scope, planIsEmpty(next) ? null : next)
  }

  const hasMore = sources.some((source) => !source.done)
  const total = sources.reduce((sum, source) => sum + source.total, 0)
  const loadMore = async () => {
    if (!hasMore || loadingMore) return
    const mine = run.current
    setLoadingMore(true)
    try {
      const { sources: next, added } = await fetchMore(asked, sources, seenRef.current)
      if (mine !== run.current) return
      setSources(next)
      setAssets((now) => [...now, ...added])
    } finally {
      setLoadingMore(false)
    }
  }

  // The rest arrives as it is scrolled to.
  const marker = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const node = marker.current
    if (!node || !hasMore || loading) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadMore()
    })
    observer.observe(node)
    return () => observer.disconnect()
  })

  /** Select N: more pages are fetched until there are that many. */
  const [selecting, setSelecting] = useState(false)
  const selectCount = async (count: number) => {
    setSelecting(true)
    try {
      let have = assets
      let from = sources
      while (have.length < count && from.some((source) => !source.done)) {
        const { sources: next, added } = await fetchMore(asked, from, seenRef.current)
        from = next
        have = [...have, ...added]
      }
      setSources(from)
      setAssets(have)
      selectMany(have.slice(0, count))
    } finally {
      setSelecting(false)
    }
  }
  const steps = [10, 20, 50, 100, 200].filter((n) => n < total)
  const shownIds = assets.map((asset) => asset.id)

  /** A different scope searches again at once: our own libraries cost nothing. */
  const chooseScope = (next: Scope) => {
    keepScope(next)
    if (asked) void search(query.trim() || asked, next)
  }
  const scopePicker = (
    <PillPicker value={scope} options={SCOPES} onChange={chooseScope} name="Which libraries" align="left" />
  )

  if (!asked) {
    return (
      <section className="mbx mbx--quick">
        <Drift pictures={backdrop} ready={driftReady} gone={false} />
        <div className="mbx__centre">
          <form
            className="mbx__card-ui"
            onSubmit={(event) => {
              event.preventDefault()
              void search()
            }}
          >
            <span className="ilib__scope">{scopePicker}</span>
            <svg className="mbx__glass" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ marginLeft: 4 }}>
              <circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.4" />
              <path d="M10.6 10.6 14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <input
              className="mbx__input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={scope === 'all' ? 'Search every library' : `Search ${SCOPES.find((one) => one.value === scope)?.label}`}
              aria-label="Search the libraries"
              autoFocus
            />
            <button type="submit" className="mbx__search" disabled={!query.trim()}>
              Search
            </button>
          </form>
        </div>
      </section>
    )
  }

  return (
    <section className="web-library" id="library">
      <form
        className="web-library__search"
        onSubmit={(event) => {
          event.preventDefault()
          void search()
        }}
      >
        <span className="ilib__scope">{scopePicker}</span>
        <SearchBar
          value={query}
          onChange={setQuery}
          placeholder={scope === 'all' ? 'Search every library' : `Search ${SCOPES.find((one) => one.value === scope)?.label}`}
        />
      </form>

      <SmartSearchChips plan={plan} understanding={understanding} onDrop={dropFromPlan} />

      {error ? <div className="ww-error">{error}</div> : null}

      <div className="web-library__toolbar">
        {total > 0 ? (
          <div className="web-select" role="group" aria-label="Select images">
            <span className="web-select__label">{selecting ? 'Selecting…' : 'Select'}</span>
            {steps.map((count) => (
              <button key={count} type="button" className="web-select__step" disabled={selecting} onClick={() => void selectCount(count)}>
                {count}
              </button>
            ))}
            <button type="button" className="web-select__step" disabled={selecting} onClick={() => void selectCount(total)}>
              All
            </button>
            <span className="web-select__of">of {total}</span>
          </div>
        ) : null}
        {selectedIds.length > 0 ? (
          <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={() => deselectMany(shownIds.concat(selectedIds))}>
            Deselect all
          </button>
        ) : null}
      </div>

      <div className="web-results">
        <AssetGrid
          assets={assets}
          selectedIds={selectedIds}
          onToggle={toggle}
          onPaint={(asset, wanted) => (wanted ? select(asset) : deselect(asset.id))}
          onLongPress={isAdmin ? setTagging : undefined}
          onView={setViewing}
          loading={loading}
          emptyHint="Nothing in any library matches those words. Try others."
        />
        {hasMore && !loading ? (
          <div className="web-library__more" ref={marker}>
            <button type="button" className="ww-btn ww-btn--secondary ww-btn--sm" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </div>

      {viewing ? (
        <PictureViewer
          src={viewing.thumbnailPath || viewing.originalPath}
          full={viewing.originalPath}
          onClose={() => setViewing(null)}
        />
      ) : null}

      {tagging ? (
        <PictureTagsDialog
          library={libraryOfAsset(tagging.id) ?? 'audit'}
          asset={tagging}
          onClose={() => setTagging(null)}
        />
      ) : null}

      {scrolled ? (
        <button
          type="button"
          className={`web-totop${selectedIds.length > 0 ? ' web-totop--raised' : ''}`}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <span aria-hidden="true">&#8593;</span>
          Top
        </button>
      ) : null}
    </section>
  )
}

/* ---------- the backdrop, kept for the next visit (2026-10-14) ---------- */

const BACKDROP_KEY = 'ww.internal.backdrop'
/** Thumbnail links are good for longer than this; an hour is safe. */
const BACKDROP_FOR_MS = 60 * 60 * 1000

function readBackdrop(): SourcePicture[] | null {
  try {
    const kept = JSON.parse(window.localStorage.getItem(BACKDROP_KEY) ?? 'null') as {
      at?: number
      pictures?: { key: string; thumb: string }[]
    } | null
    if (!kept?.at || Date.now() - kept.at > BACKDROP_FOR_MS || !Array.isArray(kept.pictures)) return null
    // Only what the drift draws: its key and its thumbnail.
    return kept.pictures.map(
      (one) => ({ key: one.key, thumb: one.thumb, url: one.thumb, title: '', source: 'library', width: 0, height: 0 }) as SourcePicture,
    )
  } catch {
    return null
  }
}

function keepBackdrop(pictures: SourcePicture[]): void {
  try {
    window.localStorage.setItem(
      BACKDROP_KEY,
      JSON.stringify({ at: Date.now(), pictures: pictures.map((one) => ({ key: one.key, thumb: one.thumb })) }),
    )
  } catch {
    /* the next visit simply waits, as before */
  }
}
